import React, { useCallback, useEffect, useState } from 'react';
import crmClient from './crmClient';
import CompanyDetail from './CompanyDetail';
import { stageLabel, STAGE_TONES, formatCurrency, formatNumber, formatDate, formatAddress, formatPhone } from './format';

const CompaniesView = ({ meta, initialStage, focusCompanyId }) => {
  const [companies, setCompanies] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [filters, setFilters] = useState({ stage: initialStage || '', category: '', state: '', search: '', last_order_after: '', last_order_before: '', sort_by: 'last_order_date', sort_dir: 'DESC' });

  // Open a specific account when deep-linked from a notification.
  useEffect(() => {
    if (focusCompanyId) {
      setSelectedId(focusCompanyId);
    }
  }, [focusCompanyId]);

  const load = useCallback(
    (offset = 0) => {
      setLoading(true);
      const params = { limit: 100, offset };
      if (filters.stage) params.stage = filters.stage;
      if (filters.category) params.category = filters.category;
      if (filters.state) params.state = filters.state;
      if (filters.search) params.search = filters.search;
      if (filters.last_order_after) params.last_order_after = filters.last_order_after;
      if (filters.last_order_before) params.last_order_before = filters.last_order_before;
      params.sort_by = filters.sort_by;
      params.sort_dir = filters.sort_dir;
      crmClient
        .listCompanies(params)
        .then(payload => {
          setCompanies((payload && payload.companies) || []);
          setPagination((payload && payload.pagination) || { total: 0, limit: 50, offset });
          setLoading(false);
        })
        .catch(() => {
          setError('Unable to load accounts.');
          setLoading(false);
        });
    },
    [filters]
  );

  useEffect(() => {
    load(0);
  }, [load]);

  const categories = (meta && meta.categories) || {};
  const stages = (meta && meta.stages) || [];
  const states = (meta && meta.states) || [];

  const updateFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));

  const canPrev = pagination.offset > 0;
  const canNext = pagination.offset + pagination.limit < pagination.total;

  return (
    <div className={`crm-companies${selectedId ? ' crm-companies--split' : ''}`}>
      <div className="crm-companies-main">
        <div className="crm-filters">
          <input
            type="search"
            placeholder="Search name, domain, city…"
            value={filters.search}
            onChange={event => updateFilter('search', event.target.value)}
          />
          <select value={filters.stage} onChange={event => updateFilter('stage', event.target.value)}>
            <option value="">All stages</option>
            {stages.map(stage => (
              <option key={stage} value={stage}>
                {stageLabel(stage)}
              </option>
            ))}
          </select>
          <select value={filters.category} onChange={event => updateFilter('category', event.target.value)}>
            <option value="">All categories</option>
            {Object.keys(categories).map(cat => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
          <select value={filters.state} onChange={event => updateFilter('state', event.target.value)}>
            <option value="">All regions</option>
            {states.map(st => (
              <option key={st} value={st}>
                {st}
              </option>
            ))}
          </select>
          <select
            value={`${filters.sort_by}:${filters.sort_dir}`}
            onChange={event => {
              const [sort_by, sort_dir] = event.target.value.split(':');
              setFilters(prev => ({ ...prev, sort_by, sort_dir }));
            }}
          >
            <option value="last_order_date:DESC">Recent order</option>
            <option value="lifetime_revenue:DESC">Top revenue</option>
            <option value="lifetime_orders:DESC">Most orders</option>
            <option value="business_name:ASC">Name A–Z</option>
          </select>
          <label className="crm-date-filter">
            <span>Last order from</span>
            <input type="date" value={filters.last_order_after} onChange={event => updateFilter('last_order_after', event.target.value)} />
          </label>
          <label className="crm-date-filter">
            <span>to</span>
            <input type="date" value={filters.last_order_before} onChange={event => updateFilter('last_order_before', event.target.value)} />
          </label>
          {(filters.last_order_after || filters.last_order_before) && (
            <button
              type="button"
              className="crm-btn crm-btn--ghost crm-btn--sm"
              onClick={() => setFilters(prev => ({ ...prev, last_order_after: '', last_order_before: '' }))}
            >
              Clear dates
            </button>
          )}
        </div>

        {error && <div className="crm-inline-error">{error}</div>}

        <div className="crm-table-wrap">
          <table className="crm-table">
            <thead>
              <tr>
                <th>Account</th>
                <th>Stage</th>
                <th className="crm-num">Lifetime rev</th>
                <th className="crm-num">Orders</th>
                <th className="crm-num">Last order</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5} className="crm-empty">
                    Loading accounts…
                  </td>
                </tr>
              )}
              {!loading && companies.length === 0 && (
                <tr>
                  <td colSpan={5} className="crm-empty">
                    No accounts match these filters. Run the merchant migration in Settings to import existing merchants.
                  </td>
                </tr>
              )}
              {!loading &&
                companies.map(company => (
                  <tr
                    key={company.crm_company_id}
                    className={selectedId === company.crm_company_id ? 'crm-row-active' : ''}
                    onClick={() => setSelectedId(company.crm_company_id)}
                  >
                    <td>
                      <strong>{company.business_name}</strong>
                      <small>
                        {company.customer_category || 'Unclassified'}
                        {company.city ? ` · ${company.city}${company.state ? ', ' + company.state : ''}` : ''}
                      </small>
                      {formatAddress(company) ? <small className="crm-cell-address">{formatAddress(company)}</small> : null}
                      {company.main_phone ? <small className="crm-cell-address">{formatPhone(company.main_phone)}</small> : null}
                    </td>
                    <td>
                      <span className={`crm-stage-pill crm-stage-pill--${STAGE_TONES[company.crm_stage] || 'slate'}`}>
                        {stageLabel(company.crm_stage)}
                      </span>
                    </td>
                    <td className="crm-num">{formatCurrency(company.lifetime_revenue)}</td>
                    <td className="crm-num">{formatNumber(company.lifetime_orders)}</td>
                    <td className="crm-num">{formatDate(company.last_order_date)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <div className="crm-pagination">
          <span>
            {pagination.total ? `${pagination.offset + 1}–${Math.min(pagination.offset + pagination.limit, pagination.total)} of ${formatNumber(pagination.total)}` : '0 accounts'}
          </span>
          <div>
            <button type="button" className="crm-btn crm-btn--ghost" disabled={!canPrev} onClick={() => load(Math.max(pagination.offset - pagination.limit, 0))}>
              Previous
            </button>
            <button type="button" className="crm-btn crm-btn--ghost" disabled={!canNext} onClick={() => load(pagination.offset + pagination.limit)}>
              Next
            </button>
          </div>
        </div>
      </div>

      {selectedId && (
        <CompanyDetail
          companyId={selectedId}
          meta={meta}
          onClose={() => setSelectedId(null)}
          onChanged={() => load(pagination.offset)}
        />
      )}
    </div>
  );
};

export default CompaniesView;
